import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'

import LiveTVHero from '../../components/LiveTVHero'
import EPG from '../../components/EPG/EPG'
import Title from '../../components/Title'
import tts from '../../lib/TTS.js'
import googleAnalytics4 from '../../lib/googleAnalytics.js'
// eslint-disable-next-line @typescript-eslint/no-var-requires
const localizedFormat = require('dayjs/plugin/localizedFormat')
dayjs.extend(localizedFormat)

import type {
  Channel,
  Listing,
  RootState,
} from '../../reducers/types/reducerTypes'
import { getPlayingListing } from '../../../../shared/helpers.js'

type LiveTVWithEPGProps = {
  channels: Channel[]
  stationName: string
  autoFocus: boolean
  setAutoFocusEPG: (value: boolean) => void
}

const LiveTVWithEPG = ({
  channels,
  stationName,
  autoFocus,
  setAutoFocusEPG,
}: LiveTVWithEPGProps) => {
  const navigate = useNavigate()
  const [channelFocused, setChannelFocused] = useState<Channel | undefined>()
  const [listingFocused, setListingFocused] = useState<Listing | undefined>()
  const readWholeScreenRef = useRef(true)

  const handleChannelEnterPress = useCallback((channel: Channel) => {
    tts.stopRead()
    navigate('/live-playback', {
      state: {
        channel,
      },
    })
  }, [])

  useEffect(() => {
    googleAnalytics4.sendPageViewEvent('Live TV')
  }, [])

  useEffect(() => {
    if (channels.length > 0) {
      const channel = channels[0]
      const playingListing = getPlayingListing(channel, dayjs(new Date()))
      setChannelFocused(channel)
      setListingFocused(playingListing)
    }
  }, [channels])

  const handleChannelFocused = useCallback(
    (channel: Channel, focusedOnListingsGrid: boolean) => {
      setChannelFocused(channel)
      if (!focusedOnListingsGrid) {
        let ttsText = `Button channel, ${channel.full_name}, press to watch live ..... move right to hear the upcomming schedule`
        ttsText = readWholeScreenRef.current
          ? `These livestreams may follow a different schedule than your on-air broadcast ..... ${ttsText}`
          : ttsText
        readWholeScreenRef.current = false
        tts.stopRead()
        tts.addTextToRead(ttsText)
      }
    },
    [setChannelFocused]
  )

  const handleListingFocused = useCallback(
    (listing: Listing, focusedOnListingsGrid: boolean) => {
      setListingFocused(listing)
      if (focusedOnListingsGrid) {
        const startTime = dayjs(listing.start_time).format('LT')
        let text = `At, ${startTime}`
        if (listing.show_title && listing.episode_title) {
          text += `, ${listing.show_title} is playing ${listing.episode_title}`
        } else if (listing.show_title && !listing.episode_title) {
          text += `, ${listing.show_title} is playing`
        } else if (!listing.show_title && listing.episode_title) {
          text += `, ${listing.episode_title} is playing`
        } else if (!listing.show_title && !listing.episode_title) {
          text += `, no schedule details available`
        }

        tts.stopRead()
        tts.addTextToRead(text)
      }
    },
    [setListingFocused]
  )

  return (
    <div className="livestream-screen__with-epg">
      <LiveTVHero channel={channelFocused} listing={listingFocused} />
      <Title className="livestream-screen__with-epg__title">
        {useMemo(
          () => (
            <>
              <div className="livestream-screen__with-epg__title-icon"></div>
              {`${stationName} Live TV Schedule`}
            </>
          ),
          []
        )}
      </Title>
      <EPG
        channels={channels}
        onChannelEnterPress={handleChannelEnterPress}
        onChannelFocused={handleChannelFocused}
        onListingFocused={handleListingFocused}
        className="livestream-screen__epg"
        autoFocus={autoFocus}
        setAutoFocusEPG={setAutoFocusEPG}
      />
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    channels: state.multilivestream.channels,
  }
}

export default connect(mapStateToProps, null)(LiveTVWithEPG)
